import { useEffect, useRef, useState } from 'react'
import { toDrillPath } from '../drilldownModel'
import { IDrillSelection, IQuestionPage } from '../models'
import { formatCount } from '../viewModel'
import styles from './ChatbotManagementReport.module.scss'
import { RemoteBadge } from '../../ChatbotAnalytics/components/RemoteBadge'

export type QuestionsLoader = (
  fromUtc: Date,
  toUtc: Date,
  path: IDrillSelection[],
  page: number
) => Promise<IQuestionPage>

interface QuestionListProps {
  path: IDrillSelection[]
  fromUtc: Date
  toUtc: Date
  loadQuestions: QuestionsLoader
}

type ListState = 'loading' | 'ready' | 'error' | 'forbidden'

const isForbidden = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { httpStatusCode?: number }).httpStatusCode === 403

export const QUESTIONS_FORBIDDEN_MESSAGE =
  'Soru ve yanıtları görmek için “Chatbot Raporları – Yönetim Raporu Soru ve Yanıtları” yetkisi gerekir.'

/**
 * Detay yolunun kesişimindeki turların bayi sorusu ve asistan yanıtı (ADR-022 §2). Metinler uzun olduğu için
 * tablo yerine kart listesi; sayfalama sunucudadır (20'şer). Yol değişince ilk sayfaya döner.
 */
export default function QuestionList({ path, fromUtc, toUtc, loadQuestions }: QuestionListProps) {
  const pathKey = toDrillPath(path)
  const [page, setPage] = useState(1)
  const [state, setState] = useState<ListState>('loading')
  const [data, setData] = useState<IQuestionPage | null>(null)
  const sequenceRef = useRef(0)
  const pathRef = useRef(path)
  pathRef.current = path

  useEffect(() => {
    setPage(1)
  }, [pathKey])

  useEffect(() => {
    const requestSequence = ++sequenceRef.current
    setState('loading')

    loadQuestions(fromUtc, toUtc, pathRef.current, page)
      .then(response => {
        if (requestSequence !== sequenceRef.current) return
        setData(response)
        setState('ready')
      })
      .catch(error => {
        if (requestSequence !== sequenceRef.current) return
        setData(null)
        setState(isForbidden(error) ? 'forbidden' : 'error')
      })

    return () => {
      sequenceRef.current += 1
    }
  }, [pathKey, page, fromUtc, toUtc, loadQuestions])

  const pageCount = data ? Math.max(1, Math.ceil(data.TotalCount / data.PageSize)) : 1

  return (
    <section className={styles.questionSection} aria-label='Sorular ve yanıtlar' data-testid='question-list'>
      <p className={styles.chartTitle}>
        Sorular ve yanıtlar{data && state === 'ready' ? ` (${formatCount(data.TotalCount)} soru)` : ''}
      </p>

      {state === 'loading' ? <p role='status'>Sorular yükleniyor.</p> : null}
      {state === 'forbidden' ? (
        <p role='alert' className={styles.rangeError}>
          {QUESTIONS_FORBIDDEN_MESSAGE}
        </p>
      ) : null}
      {state === 'error' ? (
        <p role='alert' className={styles.rangeError}>
          Sorular yüklenemedi. Lütfen tekrar deneyin.
        </p>
      ) : null}
      {state === 'ready' && data && data.TotalCount === 0 ? <p>Bu seçimde soru yok.</p> : null}

      {state === 'ready' && data && data.Items.length > 0 ? (
        <>
          <ol className={styles.questionList}>
            {data.Items.map((item, index) => (
              <li key={`${item.AskedAt}-${item.DealerSapCode}-${index}`} className={styles.questionItem}>
                <div className={styles.questionMeta}>
                  <span>{item.AskedAt}</span>
                  <span>
                    {item.DealerSapCode} – {item.DealerName}
                    {item.IsRemote ? <RemoteBadge testId={`management-question-${index}-remote`} /> : null}
                  </span>
                  <span>{item.Channel}</span>
                  <span>
                    {item.OutcomeCode} · {item.OutcomeLabel}
                  </span>
                  <span>{item.IntentLabel}</span>
                </div>
                <p className={styles.questionText}>
                  <strong>Soru:</strong> {item.Question}
                </p>
                <p className={styles.answerText}>
                  <strong>Yanıt:</strong> {item.Answer}
                </p>
                {item.CardSummary !== '—' ? (
                  <p className={styles.cardSummary}>
                    <strong>Kartlar:</strong> {item.CardSummary}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
          <div className={styles.questionPager}>
            <button
              type='button'
              className={styles.secondaryButton}
              onClick={() => setPage(current => Math.max(1, current - 1))}
              disabled={data.Page <= 1}
            >
              ‹ Önceki
            </button>
            <span>
              Sayfa {data.Page} / {pageCount}
            </span>
            <button
              type='button'
              className={styles.secondaryButton}
              onClick={() => setPage(current => Math.min(pageCount, current + 1))}
              disabled={data.Page >= pageCount}
            >
              Sonraki ›
            </button>
          </div>
        </>
      ) : null}
    </section>
  )
}
